import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { VenueId } from "../../schemas/venue";
import { venueApi } from "../../venue-api";
import { queryKeys } from "../query-keys";

// The venue's own domain. Every write ends by re-reading, so the card shows what is stored.

const keys = queryKeys.venuePortal;

export function useVenueDomains(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.domains.list(venueId) : skipToken,
		queryKey: keys.domains(venueId ?? ""),
	});
}

function useDomainMutation<TVariables, TResult>(
	venueId: VenueId,
	mutationFn: (variables: TVariables) => Promise<TResult>,
) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn,
		onSettled: () => qc.invalidateQueries({ queryKey: keys.domains(venueId) }),
	});
}

export const useClaimVenueDomain = (venueId: VenueId) =>
	useDomainMutation(venueId, (host: string) => venueApi.domains.claim(venueId, host));

export const useVerifyVenueDomain = (venueId: VenueId) =>
	useDomainMutation(venueId, (host: string) => venueApi.domains.verify(venueId, host));

export const useRemoveVenueDomain = (venueId: VenueId) =>
	useDomainMutation(venueId, (host: string) => venueApi.domains.remove(venueId, host));
